import React from "react";
import { Button, Image, Space, Typography, theme, message } from "antd";
import {
    DownloadOutlined,
    CopyOutlined,
    ReloadOutlined,
    ArrowLeftOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";
import styles from "./styles/AIToolBox.module.css";

const { Text } = Typography;

function downloadFile(url, filename = "download") {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

function ImageGallery({ urls, transparent = false }) {
    const { token } = theme.useToken();
    const list = Array.isArray(urls) ? urls : [urls];
    const isSingle = list.length === 1;

    return (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Image.PreviewGroup>
                {isSingle ? (
                    <div
                        style={{
                            borderRadius: token.borderRadiusLG,
                            overflow: "hidden",
                            border: `1px solid ${token.colorBorderSecondary}`,
                            background: transparent
                                ? undefined
                                : token.colorFillQuaternary,
                            ...(transparent ? { backgroundImage: "linear-gradient(45deg,#d4d4d4 25%,transparent 25%),linear-gradient(-45deg,#d4d4d4 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#d4d4d4 75%),linear-gradient(-45deg,transparent 75%,#d4d4d4 75%)", backgroundSize: "16px 16px", backgroundPosition: "0 0,0 8px,8px -8px,-8px 0" } : {}),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 16,
                        }}
                    >
                        <Image
                            src={list[0]}
                            alt="output"
                            style={{
                                maxWidth: "100%",
                                maxHeight: 360,
                                objectFit: "contain",
                                borderRadius: token.borderRadius,
                                display: "block",
                            }}
                        />
                    </div>
                ) : (
                    <div className={styles.imageGrid}>
                        {list.map((url, i) => (
                            <div
                                key={i}
                                className={transparent ? styles.transparentWrapper : styles.imageWrapper}
                                style={{ borderRadius: token.borderRadiusLG, overflow: "hidden" }}
                            >
                                <Image src={url} alt={`output-${i}`} style={{ width: "100%" }} />
                            </div>
                        ))}
                    </div>
                )}
            </Image.PreviewGroup>

            {!isSingle && (
                <div className={styles.downloadRow}>
                    {list.map((url, i) => (
                        <Button
                            key={i}
                            icon={<DownloadOutlined />}
                            onClick={() => downloadFile(url, `image-${i + 1}.png`)}
                        >
                            Download {i + 1}
                        </Button>
                    ))}
                </div>
            )}
        </Space>
    );
}

function TextCard({ text }) {
    const { token } = theme.useToken();

    const handleCopy = () => {
        navigator.clipboard.writeText(text)
            .then(() => message.success("Copied!"))
            .catch(() => message.error("Copy failed"));
    };

    return (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <div
                style={{
                    borderRadius: token.borderRadiusLG,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    background: token.colorFillQuaternary,
                    padding: "16px 20px",
                    minHeight: 80,
                }}
            >
                <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit", fontSize: 14, lineHeight: 1.6 }}>
                    {text}
                </pre>
            </div>
            <Button
                icon={<CopyOutlined />}
                size="large"
                onClick={handleCopy}
                block
            >
                Copy to clipboard
            </Button>
        </Space>
    );
}

function FileReadyCard() {
    const { token } = theme.useToken();
    return (
        <div
            style={{
                borderRadius: token.borderRadiusLG,
                border: `1px dashed ${token.colorBorderSecondary}`,
                background: token.colorFillQuaternary,
                padding: "32px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
            }}
        >
            <CheckCircleOutlined style={{ fontSize: 18, color: token.colorSuccess }} />
            <Text type="secondary">Your file is ready to download</Text>
        </div>
    );
}

function ToolOutputRenderer({ result, output_config, onReset, onRegenerate, isRegenerating }) {
    const { token } = theme.useToken();

    if (!result) return null;

    const { output, download_enabled } = result;
    const template = output_config?.template || result.template;
    const label = output_config?.label;

    const urls = Array.isArray(output) ? output : [output];
    const isSingleOutput = urls.length === 1;
    const isImageType = template === "image_gallery" || template === "transparent_image_display";
    const isDownloadOnly = template === "download_only";
    const showActionDownload = download_enabled && (isDownloadOnly || (isImageType && isSingleOutput));

    return (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div
                style={{
                    paddingTop: 20,
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                }}
            >
                <Text
                    style={{
                        fontWeight: 500,
                        fontSize: 13,
                        color: token.colorTextSecondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                    }}
                >
                    {label || "Result"}
                </Text>
            </div>

            {isImageType && (
                <ImageGallery urls={output} transparent={template === "transparent_image_display"} />
            )}
            {template === "text_card" && (
                <TextCard text={output} />
            )}
            {isDownloadOnly && (
                <FileReadyCard />
            )}
            {!["image_gallery", "transparent_image_display", "text_card", "download_only"].includes(template) && (
                <div
                    style={{
                        borderRadius: token.borderRadiusLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillQuaternary,
                        padding: "16px 20px",
                    }}
                >
                    <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(result, null, 2)}</pre>
                </div>
            )}

            <div style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 4 }}>
                {onReset && (
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={onReset}
                        disabled={isRegenerating}
                    >
                        Edit inputs
                    </Button>
                )}
                <div style={{ flex: 1 }} />
                {onRegenerate && (
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={onRegenerate}
                        loading={isRegenerating}
                    >
                        Try again
                    </Button>
                )}
                {showActionDownload && (
                    <Button
                        type="primary"
                        size="large"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadFile(urls[0], isDownloadOnly ? "download" : "output.png")}
                        disabled={isRegenerating}
                    >
                        Download
                    </Button>
                )}
            </div>
        </Space>
    );
}

export default ToolOutputRenderer;
